import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminMap from "./AdminMap";
import useGET from "../../services/useGET";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePolygon } from "../../hooks/usePolygon";
import NaturmarketImage from "../../assets/images/Naturmarket.png";
import { getToken } from "../../services/tokenGET";
import AdminSidebar from "../../components/layout/AdminSidebar";
import AreasListCard from "../../components/admin/AreasListCard";
import EditSelectedZoneModal from "../../components/modals/EditSelectedZoneModal";
import AddNewAreaModal from "../../components/modals/AddNewAreaModal";
import Button from "../../components/common/Button";
import { MOCK_AREAS } from "../../data/mockAreas";
import { getAllLocalMockAreasWithPins, getLocalMockPins } from "../../data/mockPins";

// Check if running locally
const IS_LOCAL_DEV = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export default function AdminAreasPage() {
    const [areas, setAreas] = useState([]);
    const [areasWithPins, setAreasWithPins] = useState([]); // Areas with their polygon pins
    const [selectedItem, setSelectedItem] = useState("");
    const [pinsUrl, setPinsUrl] = useState("");
    const [pins, setPins] = useState([]);
    const { updatePolygon, clearPolygon, updateCenter, shouldRender, render, polygon, center } = usePolygon();
    const { data: fetchedAreas, isPending } = useGET(`${process.env.REACT_APP_API_URL}/api/areas`);
    const areaNames = areas ? areas.map(area => area.name) : [];
    const { data: fetchedPins } = useGET(pinsUrl);
    const [hasCentered, setHasCentered] = useState(false);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isZoneModalOpen, setZoneModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to refresh all pins
    const [isNewAreaModalOpen, setIsNewAreaModalOpen] = useState(false);
    const [newAreaData, setNewAreaData] = useState({
        name: "",
        description: "",
        image_path: null,
    });

    // Fetch all areas' pins when areas are loaded or after a save
    const fetchAllAreaPins = useCallback(async () => {
        if (!areas || areas.length === 0) return;
        
        // Use mock data for local development
        if (IS_LOCAL_DEV) {
            const areasWithPinsData = getAllLocalMockAreasWithPins(areas);
            setAreasWithPins(areasWithPinsData);
            console.log("[Local Dev] Loaded mock areas with pins:", areasWithPinsData);
            return;
        }
        
        try {
            const areasWithPinsData = await Promise.all(
                areas.map(async (area) => {
                    try {
                        const response = await fetch(
                            `${process.env.REACT_APP_API_URL}/api/pins/${area.id}`
                        );
                        if (response.ok) {
                            const pins = await response.json();
                            return { ...area, pins: pins || [] };
                        }
                        return { ...area, pins: [] };
                    } catch (error) {
                        console.error(`Error fetching pins for area ${area.name}:`, error);
                        return { ...area, pins: [] };
                    }
                })
            );
            setAreasWithPins(areasWithPinsData);
        } catch (error) {
            console.error("Error fetching all area pins:", error);
        }
    }, [areas]);

    // Fetch all pins when areas change or refresh is triggered
    useEffect(() => {
        fetchAllAreaPins();
    }, [fetchAllAreaPins, refreshTrigger]);

    // Handle polygon saved - refresh all pins
    const handlePolygonSaved = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    useEffect(() => {
        // Use mock data for local development if no fetched areas
        if (IS_LOCAL_DEV && (!fetchedAreas || fetchedAreas.length === 0)) {
            setSelectedItem(MOCK_AREAS[0].name);
            setAreas(MOCK_AREAS);
            console.log("[Local Dev] Using mock areas:", MOCK_AREAS);
            return;
        }
        
        if (fetchedAreas) {
            setSelectedItem(fetchedAreas[0].name);
            setAreas(fetchedAreas);
        }
    }, [fetchedAreas]);

    useEffect(() => {
        if (fetchedPins) {
            setPins(fetchedPins);
        }
    }, [fetchedPins]);

    useEffect(() => {
        if (selectedItem && areas.length > 0) {
            const selectedArea = areas.find(area => area.name === selectedItem);
            if (selectedArea) {
                // Use mock data for local development
                if (IS_LOCAL_DEV) {
                    const mockPins = getLocalMockPins(selectedArea.id);
                    setPins(mockPins);
                    console.log(`[Local Dev] Loaded mock pins for ${selectedItem}:`, mockPins);
                } else {
                    setPinsUrl(`${process.env.REACT_APP_API_URL}/api/pins/${selectedArea.id}`);
                    setPins([]);
                }
            }
        }
    }, [selectedItem, areas]);

    const handleClick = (selected) => {
        clearPolygon();
        setSelectedItem(selected);
        console.log("selectedItem when clicking = ", selected);
        setPins([]);
        setZoneModalOpen(true); // Open the zone modal when a zone is selected
    };

    // Handle area selection from polygon click (same as list click)
    const handleAreaSelectFromPolygon = (areaName) => {
        clearPolygon();
        setSelectedItem(areaName);
        console.log("selectedItem when clicking polygon = ", areaName);
        setPins([]);
        setZoneModalOpen(true);
    };

    useEffect(() => {
        if (selectedItem && pins.length > 0 && !hasCentered) {
            const selectedArea = areas.find(area => area.name === selectedItem);

            if (selectedArea) {

                pins.sort((a, b) => a.pin_order - b.pin_order);
                updatePolygon(pins);


                const latitudes = pins.map(pin => pin.latitude);
                const longitudes = pins.map(pin => pin.longitude);

                const centerLatitude = latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length;
                const centerLongitude = longitudes.reduce((sum, lng) => sum + lng, 0) / longitudes.length;


                updateCenter({ lat: centerLatitude, lng: centerLongitude });

                setHasCentered(true);
            }
        }
    }, [selectedItem, pins, areas, updatePolygon, updateCenter, hasCentered]);

    useEffect(() => {
        setHasCentered(false);
    }, [selectedItem]);

    const openAddModal = () => {
        setAddModalOpen(true);
        setZoneModalOpen(false); // Close zone modal when opening add modal
    };

    const closeAddModal = () => {
        setAddModalOpen(false);
        setNewAreaData({ name: "", description: "", image_path: null });
    };

    const handleOpenNewAreaModal = () => {
        setZoneModalOpen(false); // Close edit zone modal when opening add new area modal
        setIsNewAreaModalOpen(true);
    };

    const handleCloseNewAreaModal = () => {
        setIsNewAreaModalOpen(false);
    };

    const handleAreaAdded = (newArea) => {
        // Lägg till det nya skapade området i listan med "Naturområden"
        setAreas(prevAreas => [...prevAreas, newArea]);
        // Trigga en uppdatering av alla pins
        setRefreshTrigger(prev => prev + 1);
        // Välj det nya området
        setSelectedItem(newArea.name);
    };

    const handleManageZone = () => {
        if (selectedItem) {
            const selectedArea = areas.find(area => area.name === selectedItem);
            if(selectedArea) {
                const areaId = selectedArea.id;
                const url = `/admineditmap?area=${encodeURIComponent(selectedItem)}&areaId=${encodeURIComponent(areaId)}`;
                window.location.href = url;
            }
        } else {
            alert("Vänligen välj en zon innan du fortsätter.");
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewAreaData({ ...newAreaData, image_path: file });
        }
    };

    const handleAddArea = async () => {
        const formData = new FormData();
        formData.append("name", newAreaData.name);
        formData.append("description", newAreaData.description);
        formData.append("image_path", newAreaData.image_path);

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/areas/new`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "X-CSRF-TOKEN": token,
                },
                body: formData,
            });
    
            if (!response.ok) {
                alert("Error adding the area!");
            } else {
                alert("New area added successfully!");
                closeAddModal();

                const addedAreaResponse = await response.json(); 
                const addedArea = addedAreaResponse.area;
                setAreas((prevAreas) => [...prevAreas, addedArea]);
            }
        } catch (error) {
            console.error("Error adding new area:", error);
        }
    };

    const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);

    const openRemoveModal = () => {
        if (selectedItem != null) {
            setRemoveModalOpen(true);
        }
    };

    const closeRemoveModal = () => {
        setRemoveModalOpen(false);
    };

    const handleRemoveSubmit = async () => {
        closeRemoveModal();
        try {
            // Get the area ID from the selected area name
            const selectedArea = areas.find(area => area.name === selectedItem);
            if (!selectedArea || !selectedArea.id) {
                alert("Could not find area to delete.");
                return;
            }
            const selectedAreaId = selectedArea.id;

            const token = await getToken();

            const imageFilenameResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/areas/${selectedAreaId}/images`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    'X-CSRF-TOKEN': token
                }
            });

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/areas/${selectedAreaId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    'X-CSRF-TOKEN': token
                }
            });

            if (response.status === 200) {
                if (imageFilenameResponse.status === 200) {
                    const imageData = await imageFilenameResponse.json();
                    
                    if (imageData.image_path) {
                        const imageFilename = imageData.image_path;

                        const imageResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/area-images/${imageFilename}`, {
                            method: 'DELETE',
                            credentials: 'include',
                            headers: {
                                "Content-Type": "application/json",
                                'X-CSRF-TOKEN': token
                            }
                        });
    
                        if (imageResponse.status === 200) {
                            console.log("Successfully deleted image");
                        } else {
                            console.error("Error deleting image");
                        }
                    } else {
                        console.error("Image path not found in response");
                    }
                } else {
                    console.error("Error fetching image for area");
                }

                // Update areas list
                setAreas(prevAreas => {
                    const updatedAreas = prevAreas.filter(area => area.name !== selectedItem);
                    return updatedAreas;
                });

                // Update selected item
                const remainingAreas = areas.filter(area => area.name !== selectedItem);
                if (remainingAreas.length > 0) {
                    setSelectedItem(remainingAreas[0].name);
                } else {
                    setSelectedItem("");
                }

                alert("Området har tagits bort!");
            } else {
                alert("Failed to delete area. Please try again.");
                console.error("Error:", response.statusText);
            }
        } catch(error) {
            console.error(`Failed to delete area:`, error);
            alert("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-body">
            {/* Admin Sidebar */}
            <AdminSidebar />

            {/* Full-page Google Map Background - offset by sidebar on desktop, full width on mobile */}
            {!isPending && (
                <div 
                    className="flex-1 md:ml-64 relative transition-all duration-300"
                >
                    <AdminMap
                        polygon={polygon}
                        selectedArea={selectedItem}
                        allAreas={areasWithPins}
                        onAreaSelect={handleAreaSelectFromPolygon}
                        onPolygonSaved={handlePolygonSaved}
                    />
                </div>
            )}

            {/* Floating UI Elements on top of map */}
            <div className="relative pointer-events-none">
                {/* Mobile Sidebar Toggle Tab */}
                <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="md:hidden fixed left-0 top-20 z-30 pointer-events-auto bg-white shadow-lg rounded-r-lg px-2 py-6 flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                    <svg 
                        className={`w-5 h-5 text-gray-700 transition-transform ${isSidebarOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                        Naturområden
                    </span>
                </button>

                {/* Left Sidebar - Areas List Card (Floating) */}
                <div className={`fixed left-8 top-32 w-80 pointer-events-auto transition-transform duration-300 ease-in-out max-md:left-4 max-md:w-[85%] max-md:max-w-sm max-md:z-40 md:z-20 ${isSidebarOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-[calc(100%+1rem)]'} md:left-[calc(16rem+2rem)]`}>
                    <div className="mb-3">
                        <button
                            onClick={handleOpenNewAreaModal}
                            className="w-full bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                            <span className="text-xl">+</span>
                            <span>Lägg till nytt område</span>
                        </button>
                    </div>
                    <AreasListCard 
                        areas={areas}
                        selectedItem={selectedItem}
                        onAreaSelect={handleClick}
                        isPending={isPending}
                    />
                </div>

                {/* Modals */}
                <ConfirmModal
                    isOpen={isRemoveModalOpen}
                    onSubmit={handleRemoveSubmit}
                    onClose={closeRemoveModal}
                    text={`Vill du ta bort ${selectedItem}?`}
                />

                {/* Add Area Dialog */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6 border-b">
                                <h2 className="text-xl font-bold text-gray-800">Lägg till naturområde</h2>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                                    <input
                                        autoFocus
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={newAreaData.name}
                                        onChange={(e) => setNewAreaData({ ...newAreaData, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivning</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={newAreaData.description}
                                        onChange={(e) => setNewAreaData({ ...newAreaData, description: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bild</label>
                                    <input
                                        accept="image/*"
                                        type="file"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>
                            <div className="p-6 border-t flex justify-end gap-2">
                                <button
                                    onClick={closeAddModal}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    Avbryt
                                </button>
                                <button
                                    onClick={handleAddArea}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Spara
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add New Area Modal */}
            <AddNewAreaModal
                isOpen={isNewAreaModalOpen}
                onClose={handleCloseNewAreaModal}
                onAreaAdded={handleAreaAdded}
            />

            {/* Edit Selected Zone Modal */}
            <EditSelectedZoneModal
                isOpen={isZoneModalOpen}
                selectedZone={selectedItem}
                selectedZoneId={areas.find(area => area.name === selectedItem)?.id}
                onClose={() => setZoneModalOpen(false)}
                onChangeName={(newName) => {
                    // Update local state only!
                    setAreas(prevAreas => {
                        const updatedAreas = prevAreas.map(area => {
                            if (area.name === selectedItem) {
                                return { ...area, name: newName };
                            }
                            return area;
                        });
                        return updatedAreas;
                    });

                    let ele = document.getElementById("area" + selectedItem);
                    if (ele) {
                        ele.innerText = newName;
                        ele.setAttribute("id", "area" + newName);
                    }

                    setSelectedItem(newName);
                }}
                onRemove={() => {
                    setZoneModalOpen(false);
                    openRemoveModal();
                }}
            />
        </div>
    );
}
